<template>
  <div class="wrapper" v-show="hasMedia">
    <!-- Hidden Vimeo source element -- fslightbox clones this as a custom type when the anchor is clicked -->
    <div v-if="videoUrl && isVimeo" style="display: none">
      <iframe
        id="vimeo-lightbox-grid"
        :src="vimeoLightboxUrl"
        width="1920"
        height="1080"
        allow="autoplay; fullscreen"
        allowfullscreen
      />
    </div>
    <div class="media-gallery-grid" :style="gridStyle">
      <div
        v-if="videoUrl && isVimeo"
        class="media-gallery-grid__video"
        :class="{ enlarge: displayCount > (desktop ? 2 : 4) }"
      >
        <a data-fslightbox="feature-gallery-grid" data-caption="Feature video" href="#vimeo-lightbox-grid">
          <iframe
            :src="vimeoPreviewUrl"
            width="100%"
            height="100%"
            frameborder="0"
            referrerpolicy="strict-origin-when-cross-origin"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
            allowfullscreen
            title="vimeo-player"
          />
        </a>
      </div>
      <div
        v-else-if="videoUrl"
        class="media-gallery-grid__video"
        :class="{ enlarge: displayCount > (desktop ? 2 : 4) }"
      >
        <a data-fslightbox="feature-gallery-grid" data-type="video" data-caption="Feature video" :href="videoUrl">
          <video :src="videoUrl" autoplay muted loop playsinline controls />
        </a>
      </div>
      <a
        v-for="(image, index) in filtered"
        :key="image.slug || image.url"
        data-fslightbox="feature-gallery-grid"
        data-type="image"
        :data-medium-wrapper-slug="image.slug"
        :data-caption="generateCaptionHtml(image)"
        class="media-gallery-grid__image-anchor"
        :class="{ enlarge: !videoUrl && index === 0 && displayCount > (desktop ? 2 : 4) }"
        :href="image.url"
      >
        <img :src="image.thumbnail || image.url" class="media-gallery-grid__image" :alt="image.title" />
        <slot v-if="index === 0" name="manage-photos" />
        <div
          v-if="index === filtered.length - 1 && remainCount"
          class="itinerary-gallery__image-overlay"
          :data-more-images="`${remainCount}+`"
        />
      </a>
      <a
        v-for="image in remainImages"
        class="is-hidden"
        :alt="image.title"
        :href="image.url"
        :key="`fslightbox-${image.slug || image.url}`"
        data-fslightbox="feature-gallery-grid"
        data-type="image"
        :data-medium-wrapper-slug="image.slug"
        :data-caption="generateCaptionHtml(image)"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, watch } from 'vue'
import { waitObject, preloadImage } from '@/utils/mediaGallery.js'
import { once } from 'lodash'
import { useShared } from '@/helpers/vueHelpers.js'
import { generateCaptionHtml } from '@/utils/mediaGallery.js'
import { isVimeoUrl, vimeoEmbedUrl } from '@/utils/vimeoUrl.js'

const { mobile, desktop } = useShared()
const props = defineProps({
  featureImages: Array,
  videoUrl: String,
  desktopLimit: Number,
  mobileLimit: Number,
})
const isVimeo = computed(() => isVimeoUrl(props.videoUrl))
const vimeoPreviewUrl = computed(() => vimeoEmbedUrl(props.videoUrl, { background: true }))
const vimeoLightboxUrl = computed(() => vimeoEmbedUrl(props.videoUrl))
const limit = computed(() => (mobile.value ? props.mobileLimit : props.desktopLimit))
const mediaCount = computed(() => props.featureImages.length + (props.videoUrl ? 1 : 0))
const displayCount = computed(() => Math.min(mediaCount.value, limit.value))
const displayImageCount = computed(() => displayCount.value - (props.videoUrl ? 1 : 0))
const filtered = computed(() => props.featureImages.slice(0, displayImageCount.value))
const remainImages = computed(() => props.featureImages.slice(displayImageCount.value))
const remainCount = computed(() => props.featureImages.length - filtered.value.length)
const hasMedia = computed(() => props.featureImages.length > 0 || props.videoUrl)

const gridStyle = computed(() => {
  const total = displayCount.value
  let rows = 1
  let columns = 1

  if (desktop.value) {
    rows = total > 2 ? 2 : 1
    columns = Math.min(total, 4)
  } else if (mobile.value && total > 4) {
    rows = 3
    columns = 3
  } else if (mobile.value && total > 2) {
    rows = 2
    columns = 2
  } else if (mobile.value && total > 1) {
    rows = 2
  }
  return {
    gridTemplateRows: `repeat(${rows}, 1fr)`,
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
  }
})

onMounted(async () => {
  if (hasMedia.value) {
    refreshFsLightbox() // eslint-disable-line no-undef
    const fsInstance = await waitObject(fsLightboxInstances['feature-gallery-grid']) // eslint-disable-line no-undef
    if (fsInstance) {
      fsInstance.props.onOpen = once(preloadImages)
    }
  }
})

watch(
  () => props.featureImages,
  () => {
    nextTick(() => {
      refreshFsLightbox() // eslint-disable-line no-undef
    })
  },
  { deep: true },
)

function preloadImages() {
  props.featureImages.forEach(img => preloadImage(img.url))
}
</script>

<style lang="scss" scoped>
.wrapper {
  position: relative;
  padding-bottom: 42%; /* Set ratio here */
  height: 0;

  @include mobile {
    padding-bottom: 100%;
  }
}

.media-gallery-grid {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 24px;

  @include mobile {
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(3, 1fr);
    gap: 8px;
  }

  .media-gallery-grid__image {
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 15px;
    object-fit: cover;
  }

  .media-gallery-grid__image-anchor {
    width: 100%;
    height: 100%;
    display: block;
    overflow: hidden;
    position: relative;
  }

  .enlarge {
    grid-row: span 2;
    grid-column: span 2;
  }

  .media-gallery-grid__video {
    display: block;
    position: relative;
    background: black;
    border-radius: 15px;
    overflow: hidden;

    & > a {
      display: block;
      width: 100%;
      height: 100%;
      position: absolute;
      inset: 0;
    }
  }

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .media-gallery-grid__video iframe {
    pointer-events: none;
    width: 100%;
    height: 100%;
    display: block;
  }
}
</style>
